'use client';

import { Bookmark } from 'lucide-react';
import { Button } from '@/atoms/Button/Button';
import { useRequireAuth } from '@/hooks/useRequireAuth/useRequireAuth';
import { useResourceBookmark } from '@/hooks/useResourceBookmark/useResourceBookmark';
import { cn } from '@/libs/utils/utils';
import { RESOURCE_ACTION_BUTTON_CLASS_NAME } from './ResourceActions.constants';

/**
 * The viewer's private bookmark of an external resource: only their own state (brand colour when
 * bookmarked), never a count. Signed out, it asks to sign in.
 */
export function ResourceBookmarkButton({ uri }: { uri: string }) {
  const { requireAuth } = useRequireAuth();
  const { isBookmarked, isLoading, isToggling, toggle } = useResourceBookmark(uri);

  return (
    <Button
      variant="ghost"
      size="sm"
      className={cn(RESOURCE_ACTION_BUTTON_CLASS_NAME, 'w-10', isBookmarked && 'text-brand hover:text-brand')}
      aria-label="Bookmark (private)"
      aria-pressed={isBookmarked}
      title="Bookmark (private): only you can see it"
      disabled={isLoading || isToggling}
      onClick={() => requireAuth(() => void toggle())}
      data-testid="resource-bookmark-button"
    >
      <Bookmark aria-hidden="true" className={cn(isBookmarked && 'fill-current')} />
    </Button>
  );
}
